import { computed, ref } from "vue";
import { useActivityLog } from "./useActivityLog";

export interface Toast {
  id: number;
  message: string;
  type: "item" | "xp" | "info" | "success" | "warning" | "error" | "combat-good" | "combat-bad";
  createdAt: number;
  durationMs: number;
  remainingMs: number | null;
  expiresAt: number | null;
  sticky: boolean;
}

const toasts = ref<Toast[]>([]);
let nextId = 0;
let pruneTimer: number | null = null;
const frozen = ref(false);
let freezeStartedAt = 0;
const maxVisible = ref(5);

export type ToastAddOptions = {
  durationMs?: number;
  sticky?: boolean;
};

export function useToast() {
  const activityLog = useActivityLog();

  const visibleToasts = computed(() => {
    const max = Math.max(1, Math.floor(maxVisible.value));
    const sticky = toasts.value.filter((t) => t.sticky);
    const normal = toasts.value.filter((t) => !t.sticky);

    const keptSticky = sticky.length >= max ? sticky.slice(-max) : sticky;
    const remaining = Math.max(0, max - keptSticky.length);
    const keptNormal = remaining > 0 ? normal.slice(0, remaining) : [];

    return [...keptSticky, ...keptNormal].sort((a, b) => a.id - b.id);
  });

  function enforceCapacity(max = 50) {
    const list = toasts.value;
    if (list.length <= max) return;
    const sticky = list.filter((t) => t.sticky);
    const normal = list.filter((t) => !t.sticky);
    const keptSticky = sticky.length >= max ? sticky.slice(-max) : sticky;
    const remaining = Math.max(0, max - keptSticky.length);
    const keptNormal = remaining > 0 ? normal.slice(0, remaining) : [];
    toasts.value = [...keptSticky, ...keptNormal].sort((a, b) => a.id - b.id);
  }

  function syncVisibility() {
    const now = Date.now();
    const visible = new Set(visibleToasts.value.map((t) => t.id));

    toasts.value = toasts.value.map((toast) => {
      if (toast.sticky) return toast;

      const isVisible = visible.has(toast.id);
      if (isVisible) {
        if (toast.expiresAt !== null) return toast;
        const remainingMs = toast.remainingMs ?? toast.durationMs;
        return { ...toast, remainingMs: null, expiresAt: now + remainingMs };
      }

      if (toast.expiresAt === null) return toast;
      const remainingMs = Math.max(0, toast.expiresAt - now);
      return { ...toast, expiresAt: null, remainingMs };
    });
  }

  function ensurePruneTimer() {
    if (pruneTimer) return;
    pruneTimer = window.setInterval(() => {
      pruneExpired();
    }, 250);
  }

  function stopPruneTimerIfIdle() {
    if (!pruneTimer) return;
    if (toasts.value.some((t) => !t.sticky)) return;
    window.clearInterval(pruneTimer);
    pruneTimer = null;
  }

  function pruneExpired() {
    if (frozen.value) return;
    const now = Date.now();
    toasts.value = toasts.value.filter((t) => t.expiresAt === null || t.expiresAt > now);
    syncVisibility();
    stopPruneTimerIfIdle();
  }

  function addToast(message: string, type: Toast["type"] = "info", durationOrOptions: number | ToastAddOptions = 3000) {
    const now = Date.now();
    const options: ToastAddOptions = typeof durationOrOptions === "number" ? { durationMs: durationOrOptions } : durationOrOptions;
    const sticky = !!options.sticky;
    const durationMs = Math.max(0, Math.floor(Number(options.durationMs ?? 3000)));

    const id = nextId++;
    const toast: Toast = {
      id,
      message,
      type,
      createdAt: now,
      durationMs,
      remainingMs: sticky ? null : durationMs,
      expiresAt: null,
      sticky
    };

    toasts.value = [...toasts.value, toast];
    enforceCapacity(200);
    activityLog.add(type, message);
    syncVisibility();
    ensurePruneTimer();
    return id;
  }

  function removeToast(id: number) {
    toasts.value = toasts.value.filter((t) => t.id !== id);
    syncVisibility();
    stopPruneTimerIfIdle();
  }

  function setMaxVisible(next: number) {
    maxVisible.value = Math.max(1, Math.floor(Number(next) || 1));
    syncVisibility();
  }

  function setFrozen(next: boolean) {
    if (next === frozen.value) return;
    if (next) {
      frozen.value = true;
      freezeStartedAt = Date.now();
      return;
    }

    const delta = Date.now() - freezeStartedAt;
    frozen.value = false;
    if (delta <= 0) return;
    toasts.value = toasts.value.map((t) =>
      t.expiresAt === null ? t : { ...t, expiresAt: t.expiresAt + delta }
    );
  }

  function showItemGain(itemName: string, quantity: number) {
    addToast(`+${quantity} ${itemName}`, "item");
  }

  function showXpGain(skillId: string, amount: number) {
    const skillName = skillId.charAt(0).toUpperCase() + skillId.slice(1);
    addToast(`+${amount} ${skillName} XP`, "xp");
  }

  function showCombatGood(message: string, duration = 4500) {
    addToast(message, "combat-good", duration);
  }

  function showCombatBad(message: string, duration = 4500) {
    addToast(message, "combat-bad", duration);
  }

  return {
    toasts,
    visibleToasts,
    addToast,
    removeToast,
    setMaxVisible,
    setFrozen,
    showItemGain,
    showXpGain,
    showCombatGood,
    showCombatBad
  };
}

